import Button from 'common/Button';
import ButtonSecondary from 'common/ButtonSecondary';
import { HomePageIds } from 'common/constant';
import routes from 'config/routes';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LogoDark from "../../../common/Icons/LogoDark.svg"
import { smoothScrollTo } from 'utils/smoothScroll';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';

const drawerWidth = 280;

import { useRecoilState } from 'recoil';
import { userDetails as userDetailsAtom } from 'atoms';
import {
    DashboardOutlined,
    HelpOutlineOutlined,
    LogoutOutlined
} from "@mui/icons-material";
import { logout } from 'components/auth/auth';

export default function Header() {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    const [user, setUser] = useRecoilState(userDetailsAtom)
    const isLoggedIn = user?.session?.access_token && user?.user?.id

    const navBarItems = [
        { id: 1, name: 'Why Be:Seen?', route: HomePageIds.whyBeSeen },
        { id: 2, name: 'How It Works', route: HomePageIds.howItWorks },
        { id: 3, name: 'Pricing', route: HomePageIds.pricing },
        { id: 4, name: 'Top FAQs', route: HomePageIds.faqs },
    ];
    const scrollToSection = (sectionId) => {
        const sectionElement = document.getElementById(sectionId);

        if (sectionElement) {
            const yOffset = -80;
            const targetY =
                sectionElement.getBoundingClientRect().top +
                window.pageYOffset +
                yOffset;

            smoothScrollTo(targetY, 400);
        }
    };

    const handleLogout = async () => {
        const { success, error } = await logout();
        if (success) {
            localStorage.clear()
            navigate(routes.auth.login);
            window.location.reload();
            setUser([])
        } else {
            console.error(error.message);
        }
    };

    return (
        <header className="bg-white flex items-center p-4 md:px-8 lg:px-16 relative min-h-[72px] w-full">
            {/* Logo */}
            <div className="flex items-center">
                <img src={LogoDark} alt="Logo" width={90} height={30} />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex gap-5 lg:gap-10 text-[#999] text-sm ml-12">
                {navBarItems.map((item) => (
                    <a
                        key={item.id}
                        onClick={() => scrollToSection(item.route)}
                        className="cursor-pointer hover:underline"
                    >
                        {item.name}
                    </a>
                ))}
            </nav>

            {/* Mobile Menu Button - Right Aligned */}
            <button
                className="md:hidden text-gray-700 focus:outline-none ml-auto flex-shrink-0 min-w-[40px] min-h-[40px] flex items-center justify-center"
                onClick={() => setMenuOpen(!menuOpen)}
            >
                {menuOpen ? <CloseIcon style={{ fontSize: '24px' }} /> : <MenuIcon style={{ fontSize: '24px' }} />}
            </button>

            {/* Desktop Buttons */}
            {isLoggedIn ? (
                <div className="relative hidden md:flex items-center ml-auto">
                    <div
                        className="cursor-pointer flex items-center gap-2"
                        onClick={() => setUserMenuOpen(!userMenuOpen)}
                    >
                        <MenuIcon fontSize="large" className="text-[#999]" />
                    </div>

                    {userMenuOpen && (
                        <div className="absolute top-full right-0 mt-2 bg-white shadow-md rounded-md w-40 flex flex-col z-50">
                            <a
                                onClick={() => navigate(routes.panel.business)}
                                className="flex items-center gap-2 cursor-pointer px-4 py-2 hover:bg-gray-100"
                            >
                                <DashboardOutlined fontSize="small" className="text-gray-600" />
                                Dashboard
                            </a>
                            <a
                                href="https://beseenhealth.crunch.help/en"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 cursor-pointer px-4 py-2 hover:bg-gray-100"
                            >
                                <HelpOutlineOutlined fontSize="small" className="text-gray-600" />
                                Help Center
                            </a>
                            <button
                                onClick={handleLogout}
                                className="flex items-center gap-2 cursor-pointer px-4 py-2 text-red-500 hover:bg-gray-100"
                            >
                                <LogoutOutlined fontSize="small" className="text-red-500" />
                                Logout
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="hidden md:flex gap-3 ml-auto items-center">
                    <a
                        href='https://beseenhealth.crunch.help/en'
                        className="cursor-pointer hover:underline text-[#999] text-sm mx-2"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Help Center
                    </a>
                    <ButtonSecondary onClick={() => navigate(routes.auth.login)}>Sign In</ButtonSecondary>
                    <Button onClick={() => navigate(routes.auth.register)}>Join Now</Button>
                </div>
            )}

            {/* Mobile Navigation Drawer - Right Side */}
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        minWidth: drawerWidth,
                        maxWidth: drawerWidth,
                        boxSizing: 'border-box',
                        padding: '20px',
                        backgroundColor: '#ffffff',
                        flexShrink: 0,
                    },
                }}
                variant="temporary"
                anchor="right"
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
            >
                <Box className="flex flex-col h-full w-full">
                    {/* Close button */}
                    <div className="flex justify-end mb-6">
                        <button
                            onClick={() => setMenuOpen(false)}
                            className="text-gray-700 focus:outline-none flex-shrink-0 min-w-[24px] min-h-[24px] flex items-center justify-center"
                        >
                            <CloseIcon style={{ fontSize: '24px' }} />
                        </button>
                    </div>

                    {/* Navigation Links */}
                    <div className="flex flex-col gap-5" style={{ gap: '20px' }}>
                        {navBarItems.map((item) => (
                            <a
                                key={item.id}
                                onClick={() => {
                                    scrollToSection(item.route);
                                    setMenuOpen(false);
                                }}
                                className="cursor-pointer hover:underline text-[#999] text-lg font-medium flex-shrink-0 whitespace-nowrap"
                                style={{ fontSize: '18px', flexShrink: 0, minWidth: 'fit-content' }}
                            >
                                {item.name}
                            </a>
                        ))}
                        <a
                            href='https://beseenhealth.crunch.help/en'
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cursor-pointer hover:underline text-[#999] text-lg font-medium flex-shrink-0 whitespace-nowrap"
                            style={{ fontSize: '18px', flexShrink: 0, minWidth: 'fit-content' }}
                        >
                            Help Center
                        </a>
                    </div>

                    {/* Divider Line */}
                    <div className="w-full border-b border-gray-200 my-6"></div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-3">
                        {isLoggedIn ? (
                            <>
                                <ButtonSecondary onClick={() => {
                                    navigate(routes.panel.business);
                                    setMenuOpen(false);
                                }}>
                                    Dashboard
                                </ButtonSecondary>
                                <Button onClick={() => {
                                    handleLogout();
                                    setMenuOpen(false);
                                }}>
                                    Logout
                                </Button>
                            </>
                        ) : (
                            <>
                                <ButtonSecondary onClick={() => {
                                    navigate(routes.auth.login);
                                    setMenuOpen(false);
                                }}>
                                    Sign In
                                </ButtonSecondary>
                                <Button onClick={() => {
                                    navigate(routes.auth.register);
                                    setMenuOpen(false);
                                }}>
                                    Join Now
                                </Button>
                            </>
                        )}
                    </div>
                </Box>
            </Drawer>
        </header>
    );
}
